import { useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Pagination,
  SearchInput,
  Select,
  Table,
  type Column,
} from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { formatDateTime } from '@/internationalization/format'
import { tt, useT } from '@/internationalization/i18n'
import type { AuditEvent, AuditFacets, AuditFilters, AuditPage } from '@/types'

const PAGE_SIZE = 50

/**
 * Append-only record of who did what.
 *
 * TWO READERS, ONE SCREEN. Everybody sees their own history -- that needs no
 * permission, and somebody who cannot see their own actions cannot notice one
 * attributed to them that they did not take. `audit:read` widens it to the
 * whole workspace and turns on the "whose log" control.
 *
 * WHICH OF THE TWO IS THE SERVER'S ANSWER, NOT THIS FILE'S. `scope` comes back
 * on the response. It could be derived from the permission list, and that would
 * be a second copy of a rule enforced in SQL -- the copy that drifts is always
 * the client's, and here it would drift into showing a filter that silently
 * returns nothing.
 */
export function AuditLogPage() {
  const t = useT()
  const [filters, setFilters] = useState<AuditFilters>({})
  const [page, setPage] = useState(1)

  // `page` is deliberately not part of `filters`: changing a FILTER must send
  // you back to page 1, or you land on page 7 of a three-page result and read
  // an empty table as "nothing matched".
  const query = useMemo(
    () => ({ ...filters, page, pageSize: PAGE_SIZE }),
    [filters, page],
  )

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: qk.admin.audit(query),
    queryFn: () => api.get<AuditPage>('/admin/audit', { query }),
    // The table would otherwise blank out on every keystroke and page change,
    // which reads as "no results" for as long as the request takes.
    placeholderData: keepPreviousData,
  })

  const { data: facets } = useQuery({
    queryKey: qk.admin.auditFacets,
    queryFn: () => api.get<AuditFacets>('/admin/audit/facets'),
    staleTime: 60_000,
  })

  const scope = data?.scope ?? facets?.scope
  const set = (patch: AuditFilters) => {
    setFilters((current) => ({ ...current, ...patch }))
    setPage(1)
  }
  const clear = () => {
    setFilters({})
    setPage(1)
  }
  const active = Object.values(filters).some((value) => value)

  const columns: Column<AuditEvent>[] = [
    {
      id: 'at',
      header: t('audit.col.when'),
      width: 'w-44',
      cell: (row) => <span className="text-fg-muted">{formatDateTime(row.at)}</span>,
    },
    { id: 'actor', header: t('audit.col.actor'), cell: (row) => row.actor },
    {
      id: 'action',
      header: t('audit.col.action'),
      cell: (row) => <code className="font-mono text-sm text-fg">{row.action}</code>,
    },
    {
      id: 'target',
      header: t('audit.col.target'),
      hideOnMobile: true,
      cell: (row) => (
        <span>
          {row.target}
          {row.targetType && (
            <Badge className="ml-2" size="sm">
              {row.targetType}
            </Badge>
          )}
        </span>
      ),
    },
    {
      id: 'detail',
      header: t('audit.col.detail'),
      hideOnMobile: true,
      // The one column that earns its place on a role edit: "the Operations
      // role was updated" answers nothing, `+meeting:read_all` answers the
      // question somebody opened this screen with.
      cell: (row) => <span className="text-sm text-fg-muted">{summarise(row)}</span>,
    },
    {
      id: 'ip',
      header: t('audit.col.ip'),
      width: 'w-36',
      hideOnMobile: true,
      cell: (row) => (
        <span className="font-mono text-sm text-fg-subtle">{row.ip ?? '—'}</span>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title={t('audit.title')}
        description={scope === 'self' ? t('audit.descSelf') : t('audit.descWorkspace')}
        below={
          <div className="flex flex-col gap-3">
            {/*
              ONE ROW, and every control is wrapped in a sized div rather than
              given a `className`. `Select` and `Input` render their own
              `w-full` outer wrapper and pass `className` down to the inner
              `<select>`/`<input>`, so sizing them that way left each wrapper at
              full width and every field claimed its own flex line -- six
              stacked rows that pushed the table below the fold. `SearchInput`
              merges `className` into its wrapper, which is why that one alone
              looked right and made the cause easy to miss.
            */}
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
              <div className="w-64 shrink-0">
                <SearchInput
                  value={filters.q ?? ''}
                  onChange={(value) => set({ q: value || undefined })}
                  placeholder={t('audit.searchPlaceholder')}
                />
              </div>
              {scope === 'workspace' && (
                <div className="w-52 shrink-0">
                  <Select
                    label={t('audit.filter.whose')}
                    value={filters.actor ?? 'all'}
                    onChange={(event) =>
                      set({
                        actor:
                          event.target.value === 'all'
                            ? undefined
                            : event.target.value,
                      })
                    }
                    options={[
                      { value: 'all', label: t('audit.filter.everyone') },
                      ...(facets?.actors ?? []).map((actor) => ({
                        value: actor.id,
                        label: t('audit.actorEvents', { name: actor.name, count: actor.events }),
                      })),
                    ]}
                  />
                </div>
              )}
              <div className="w-40 shrink-0">
                <Select
                  label={t('audit.filter.area')}
                  value={filters.category ?? ''}
                  onChange={(event) =>
                    set({
                      category: event.target.value || undefined,
                      action: undefined,
                    })
                  }
                  options={[
                    { value: '', label: t('audit.filter.allAreas') },
                    ...(facets?.categories ?? []).map((key) => ({
                      value: key,
                      label: key,
                    })),
                  ]}
                />
              </div>
              <div className="w-56 shrink-0">
                <Select
                  label={t('audit.filter.action')}
                  value={filters.action ?? ''}
                  onChange={(event) =>
                    set({ action: event.target.value || undefined })
                  }
                  options={[
                    { value: '', label: t('audit.filter.allActions') },
                    ...(facets?.actions ?? [])
                      .filter(
                        (a) =>
                          !filters.category ||
                          a.key.startsWith(`${filters.category}.`),
                      )
                      .map((a) => ({
                        value: a.key,
                        label: `${a.key} (${a.events})`,
                      })),
                  ]}
                />
              </div>
              <div className="w-40 shrink-0">
                <Input
                  label={t('audit.filter.from')}
                  type="date"
                  value={toInputDate(filters.since)}
                  onChange={(event) => set({ since: dayStart(event.target.value) })}
                />
              </div>
              <div className="w-40 shrink-0">
                <Input
                  label={t('audit.filter.to')}
                  type="date"
                  value={toInputDate(filters.until, -1)}
                  onChange={(event) => set({ until: dayAfter(event.target.value) })}
                />
              </div>
              {active && (
                <Button variant="ghost" onClick={clear}>
                  {t('common.clearFilters')}
                </Button>
              )}
            </div>
            {scope === 'self' && (
              <Alert tone="info" title={t('audit.selfTitle')}>
                {t('audit.selfBodyPrefix')}
                <code className="font-mono">audit:read</code>
                {t('audit.selfBodySuffix')}
              </Alert>
            )}
          </div>
        }
      />
      <Card>
        <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
          <div aria-busy={isFetching}>
            <Table
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(row) => row.id}
              empty={
                <p className="px-4 py-10 text-center text-sm text-fg-muted">
                  {active
                    ? t('audit.empty.filtered')
                    : scope === 'self'
                      ? t('audit.empty.self')
                      : t('audit.empty.workspace')}
                </p>
              }
            />
            <Pagination
              page={data?.page ?? 1}
              pageSize={data?.pageSize ?? PAGE_SIZE}
              total={data?.total ?? 0}
              onPageChange={setPage}
            />
          </div>
        </QueryBoundary>
      </Card>
    </div>
  )
}

/**
 * The one-line "so what" of a row, from its metadata.
 *
 * Read defensively on purpose: `metadata` is a jsonb column whose shape is
 * decided by whichever writer produced the row, and a screen that throws on an
 * unfamiliar action would take the whole audit log down rather than render one
 * row plainly.
 */
function summarise(row: AuditEvent): string {
  const meta = row.metadata ?? {}
  const parts: string[] = []
  const granted = asStrings(meta.granted)
  const revoked = asStrings(meta.revoked)
  if (granted.length) parts.push(granted.map((p) => `+${p}`).join(' '))
  if (revoked.length) parts.push(revoked.map((p) => `−${p}`).join(' '))
  if (typeof meta.renamedFrom === 'string') parts.push(tt('audit.wasNamed', { name: meta.renamedFrom }))
  if (typeof meta.method === 'string') parts.push(String(meta.method))
  if (typeof meta.reason === 'string') parts.push(String(meta.reason))
  return parts.join(' · ')
}

function asStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

/** Midnight local, as an instant the API can compare against `occurred_at`. */
function dayStart(value: string): string | undefined {
  return value ? new Date(`${value}T00:00:00`).toISOString() : undefined
}

/**
 * The day AFTER the one picked, because `until` is exclusive.
 *
 * A person choosing "to 30 September" means the whole of the 30th. Passing
 * midnight ON the 30th silently drops that day's events, which is the kind of
 * off-by-one that makes somebody conclude nothing happened.
 */
function dayAfter(value: string): string | undefined {
  if (!value) return undefined
  const next = new Date(`${value}T00:00:00`)
  next.setDate(next.getDate() + 1)
  return next.toISOString()
}

/**
 * An instant back into the `YYYY-MM-DD` a date input wants, in LOCAL time.
 *
 * `.toISOString().slice(0, 10)` is the obvious version and is wrong east of
 * Greenwich: local midnight on the 30th is 18:30Z on the 29th, so the field
 * would redisplay the day before the one that was picked and the range would
 * appear to drift a day every time the page reloaded.
 *
 * `shiftDays` undoes the exclusive-end conversion for the "To" field, which
 * stores the day AFTER the one the person chose.
 */
function toInputDate(value: string | undefined, shiftDays = 0): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  date.setDate(date.getDate() + shiftDays)
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}
